// ─────────────────────────────────────────────────────────────────────────────
// TEMPORARY — measurement, not a feature. Remove before launch (see the note
// at the bottom of this file for exactly what to delete).
//
// Every speed fix made so far was reasoned out of the code, never measured, so
// nobody actually knows which screen costs what. This keeps a tally of how long
// each kind of request takes during real use, and the /velocita page shows it.
//
// It records ENDPOINTS, never content: the url is stripped of ids and of the
// whole query string before being counted, so no title, date, email or event id
// is ever stored. Nothing is sent anywhere — it lives in this browser only.
// ─────────────────────────────────────────────────────────────────────────────

const STORE_KEY = 'memo_perf_v1';
const MAX_KEYS = 120;          // a runaway key set would be a bug, not data
let stats = load();
let saveTimer = null;

function load() {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (_) {
    return {};
  }
}

function scheduleSave() {
  if (saveTimer) return;
  saveTimer = setTimeout(() => {
    saveTimer = null;
    try { localStorage.setItem(STORE_KEY, JSON.stringify(stats)); } catch (_) {}
  }, 3000);
}

// '/groups/9f2c.../events?date=2026-08-14' → '/groups/:id/events'
// Anything that looks like an id — a uuid, a long token, a number, a Google
// event id — becomes ':id'. What is left is the shape of the request.
export function normalisePath(url) {
  let p = String(url || '').split('?')[0];
  p = p.replace(/\/+$/, '') || '/';
  return p
    .split('/')
    .map(seg => {
      if (!seg) return seg;
      if (/^\d+$/.test(seg)) return ':id';
      if (/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(seg)) return ':id';   // uuid
      // Provider ids. "long" alone was not enough to tell an id from a route:
      // 'subcalendars' is twelve characters, so a real endpoint was being
      // reported as GET /:id and the report could not say what was slow.
      // An id carries digits, or is longer than any endpoint name would be.
      if (seg.includes('.')) return seg;
      if (seg.length >= 12 && /\d/.test(seg)) return ':id';
      if (seg.length >= 20) return ':id';
      return seg;
    })
    .join('/');
}

/**
 * Record one request.
 * @param {string} method  GET / POST / …
 * @param {string} url     the request url (ids and query are stripped here)
 * @param {number} ms      how long the app waited, in milliseconds
 * @param {object} extra   { cached?: boolean, shared?: boolean, serverMs?: number, failed?: boolean }
 */
export function recordRequest(method, url, ms, extra = {}) {
  try {
    const key = `${String(method || 'GET').toUpperCase()} ${normalisePath(url)}`;
    if (!stats[key] && Object.keys(stats).length >= MAX_KEYS) return;
    const s = stats[key] || (stats[key] = {
      calls: 0, totalMs: 0, worstMs: 0,
      cached: 0, shared: 0, failed: 0,
      serverCalls: 0, serverMs: 0,
    });
    s.calls += 1;
    if (extra.cached) { s.cached += 1; return; }   // a cache hit costs nothing
    if (extra.shared) { s.shared += 1; }
    if (extra.failed) { s.failed += 1; }
    const wait = Math.max(0, Math.round(ms));
    s.totalMs += wait;
    if (wait > s.worstMs) s.worstMs = wait;
    if (Number.isFinite(extra.serverMs)) {
      s.serverCalls += 1;
      s.serverMs += Math.max(0, Math.round(extra.serverMs));
    }
    scheduleSave();
  } catch (_) {
    // Measuring must never be able to break the thing it measures.
  }
}

/** Rows sorted by the total time actually spent waiting — the real cost. */
export function getReport() {
  return Object.entries(stats)
    .map(([key, s]) => {
      const real = s.calls - s.cached;         // trips that actually happened
      return {
        key,
        calls: s.calls,
        cached: s.cached,
        shared: s.shared,
        failed: s.failed,
        totalMs: s.totalMs,
        worstMs: s.worstMs,
        avgMs: real > 0 ? Math.round(s.totalMs / real) : 0,
        avgServerMs: s.serverCalls > 0 ? Math.round(s.serverMs / s.serverCalls) : null,
      };
    })
    .sort((a, b) => b.totalMs - a.totalMs);
}

export function resetReport() {
  stats = {};
  try { localStorage.removeItem(STORE_KEY); } catch (_) {}
}

// ── To remove before launch ──────────────────────────────────────────────────
// 1. this file
// 2. the four recordRequest() calls in api/client.js (and its import)
// 3. components/SpeedReport.jsx and its /velocita route in App.jsx
// 4. the X-Memo-Ms middleware and exposedHeaders in server/src/index.js
